<template>
  <div>
    <div class="row">
      <div v-if="internalValue" class="col-auto align-self-start pr-0">
        <font-awesome-layers full-width class="fa-2x text-secondary">
          <font-awesome-icon icon="circle" />
          <font-awesome-layers-text class="col-form-label-sm btn-secondary" style="background-color: transparent"
            :value="getUserInitials(internalValue.CreatedBy.FirstName, internalValue.CreatedBy.LastName)" />
        </font-awesome-layers>
      </div>
      <div class="col">
        <div v-if="internalValue" class="row align-items-center">
          <div class="col">
            <span class="col-form-label-sm text-muted" :style="{ 'text-decoration': internalValue.IsDeleted ? 'line-through' : null }">
              {{ internalValue.CreatedBy.FirstName ?? $t('Webportal.Unknown') }} {{ internalValue.CreatedBy.LastName }}
              <span v-if="internalValue.CreatedBy.Email">({{ internalValue.CreatedBy.Email }})</span>
            </span>

            <span v-if="canEdit" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.EditComment')">
              <b-button :disabled="isEditMode" pill variant="light" size="sm" @click="editComment()">
                <font-awesome-icon :icon="['far', 'edit']" />
              </b-button>
            </span>
            <span v-if="canDelete" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.DeleteComment')">
              <b-button :disabled="isEditMode" pill variant="light" size="sm" @click="deleteComment()">
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </b-button>
            </span>
          </div>
          <div class="col-auto col-form-label-sm">
            <div v-if="internalValue.IsDeleted" class="text-danger">
              {{ $t("Webportal.Deleted") }}
            </div>
            <div v-else-if="showModifiedHint && internalValue.IsModified">
              {{ $t("Webportal.Modified") }}
            </div>
          </div>
          <div class="col-3 text-right">
            <span class="col-form-label-sm text-muted">{{ internalValue.CreatedAt | msm_datetime_long }}</span>
          </div>
        </div>

        <template v-if="isCreateMode || isEditMode">
          <b-form-textarea v-model="newCommentText" :placeholder="$t('Webportal.Comment.Placeholder')" class="app-ctrl"
            max-rows="6" />

          <b-row class="mt-2">
            <b-col v-if="templateSelectionVisible" cols="auto">
              <b-dropdown :text="$t('Webportal.Actions.SelectCommentTemplate')" variant="outline-primary" size="sm" :disabled="templateSelectionDisabled">
                <b-dropdown-item v-for="template in templates" :key="template.id" @click="newCommentText = (newCommentText ? newCommentText + '\r\n' : '') + template.text">
                  <div>{{ template.title }}</div>
                  <div class="small text-muted text-truncate" style="max-width: 400px;">{{ template.text }}</div>
                </b-dropdown-item>
              </b-dropdown>
            </b-col>
            <b-col cols="auto" class="ml-auto px-1">
            <b-button v-show="isEditMode" variant="secondary" size="sm" @click="cancel()" :disabled="isBusy">
                <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
              </b-button>
            </b-col>
            <b-col cols="auto" class="pl-1">
              <b-button variant="secondary" size="sm" style="width: 100px" :disabled="isBusy || !newCommentText"
                @click="sendComment(false)">
                {{ $t("Webportal.Send") }} <b-spinner v-show="isBusy" small></b-spinner>
              </b-button>
            </b-col>
          </b-row>
        </template>
        <p :style="{ 'text-decoration': internalValue.IsDeleted ? 'line-through' : null }" v-else>
          {{ internalValue.Comment }}
        </p>
      </div>
    </div>
  </div>
</template>


<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';

export default {
  name: "Comment",
  mixins: [AppConfig],

  props: {
    surveyRevisionId: { type: Number, default: null },
    surveyRevisionEntryId: { type: Number, default: null },
    value: { type: Object, default: null },
    allowEdit: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    showModifiedHint: { type: Boolean, default: false },
    templates: { type: Array, default: null },
    templateSelectionVisible: { default: false },
    templateSelectionDisabled: { default: false },
    onSend: { type: Function, default: null },
  },

  data() {
    return {
      newCommentText: null,
      internalValue: this.value,
      isEditMode: false,

      isBusy: false,
    };
  },

  watch: {
    isBusy(newVal, oldVal) {
      this.$emit("is-busy-changed", newVal);
    },
  },

  computed: {
    isCreateMode() {
      return this.internalValue == null;
    },

    canEdit() {
      return this.allowEdit && !this.internalValue.IsDeleted;
    },

    canDelete() {
      return this.allowDelete && !this.internalValue.IsDeleted;
    }
  },

  methods: {
    getUserInitials: function (firstName, lastName) {
      if (firstName && lastName)
        return (
          firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
        );
      else return "";
    },

    editComment() {
      this.newCommentText = this.internalValue.Comment;
      this.isEditMode = true;
    },

    async deleteComment() {
      const dialogResult = await this.$bvModal.msgBoxConfirm(
        this.$t("Webportal.Dialog.ConfirmDeleteComment.Text"),
        {
          title: this.$t("Webportal.Dialog.ConfirmDeleteComment.Title"),
          okTitle: this.$t("Webportal.Dialog.Ok"),
          cancelTitle: this.$t("Webportal.Dialog.Cancel"),
          hideHeaderClose: false,
        }
      );

      if (dialogResult) {
        await this.sendComment(true);
      }
    },

    cancel() {
      this.isEditMode = false;
      this.newCommentText = null;
    },

    async sendComment(isDeleted) {
      if (this.onSend && typeof this.onSend === 'function') {
        this.isBusy = true;

        try {
          await this.onSend(this.internalValue, this.newCommentText, isDeleted);

          this.isEditMode = false;
          this.newCommentText = null;
        } finally {
          this.isBusy = false;
        }

        this.isBusy = false;
      }
    },
  },
}
</script>